import { computed } from 'vue'
import { useColors } from 'vuestic-ui'
import { appColors, darkModeColors, type AppColors } from '../config/theme'

/**
 * Theme composable for accessing colors and theme utilities
 * Now synced with Vuestic UI's theme system
 */
export const useTheme = () => {
  const { currentPresetName, applyPreset } = useColors()

  const isDarkMode = computed(() => currentPresetName.value === 'dark')

  const colors = computed<AppColors>(() => {
    if (isDarkMode.value) {
      return { ...appColors, ...darkModeColors }
    }
    return appColors
  })

  const toggleDarkMode = () => {
    const newTheme = isDarkMode.value ? 'light' : 'dark'
    applyPreset(newTheme)
    updateCSSVariables()
  }

  const setDarkMode = (dark: boolean) => {
    const newTheme = dark ? 'dark' : 'light'
    applyPreset(newTheme)
    updateCSSVariables()
  }

  const updateCSSVariables = () => {
    const root = document.documentElement
    const currentColors = colors.value

    // Update CSS custom properties
    root.style.setProperty('--app-primary', currentColors.primary)
    root.style.setProperty('--app-primary-light', currentColors.primaryLight)
    root.style.setProperty('--app-primary-dark', currentColors.primaryDark)
    root.style.setProperty('--app-secondary', currentColors.secondary)
    root.style.setProperty('--app-success', currentColors.success)
    root.style.setProperty('--app-warning', currentColors.warning)
    root.style.setProperty('--app-danger', currentColors.danger)
    root.style.setProperty('--app-info', currentColors.info)
    root.style.setProperty('--app-background', currentColors.background)
    root.style.setProperty('--app-background-secondary', currentColors.backgroundSecondary)
    root.style.setProperty('--app-text-primary', currentColors.textPrimary)
    root.style.setProperty('--app-text-secondary', currentColors.textSecondary)
    root.style.setProperty('--app-border', currentColors.border)
    root.style.setProperty('--app-brand', currentColors.brand)
    root.style.setProperty('--app-accent', currentColors.accent)
    root.style.setProperty('--app-church', currentColors.church)
    root.style.setProperty('--app-sacrament', currentColors.sacrament)
  }

  // Helper functions for common color operations
  const getColorWithOpacity = (color: string, opacity: number): string => {
    // Convert hex to rgba
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  const getBrandColor = () => colors.value.brand
  const getPrimaryColor = () => colors.value.primary
  const getSuccessColor = () => colors.value.success
  const getWarningColor = () => colors.value.warning
  const getDangerColor = () => colors.value.danger

  return {
    colors,
    isDarkMode,
    toggleDarkMode,
    setDarkMode,
    updateCSSVariables,
    getColorWithOpacity,
    getBrandColor,
    getPrimaryColor,
    getSuccessColor,
    getWarningColor,
    getDangerColor,
  }
}
